'use client'

/* eslint-disable @typescript-eslint/no-explicit-any, max-lines-per-function, complexity, security/detect-object-injection, security/detect-non-literal-regexp, react-hooks/exhaustive-deps */
// Smart search requires flexible data handling, complex filtering logic, and dynamic regex patterns

import React, { useState, useEffect, useRef, useCallback, ReactNode, forwardRef } from 'react'
import { Search, X, Clock, AlertTriangle, CheckCircle, Filter, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * BIRHAUS Smart Search Component
 * 
 * Universal intelligent search implementing BIRHAUS principles:
 * - Miller's Law: Max 7±2 items in suggestion list (enforced)
 * - Progressive disclosure: Advanced filters only when needed
 * - Spanish-first labels and messages
 * - Debounced search with fuzzy matching capabilities
 * - Recent searches and intelligent suggestions
 * - Multi-select support with cognitive load management
 * - Accessibility-first design (WCAG AA+)
 * - Generic item rendering for any search scenario
 * - Undo-over-confirm pattern (clear search without confirmation)
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | string[] | undefined | null | false)[]): string {
  return classes.flat().filter(Boolean).join(' ')
}

export interface BirhausSearchItem {
  id: string
  type: string
  title: string
  subtitle?: string
  description?: string
  metadata?: Record<string, any>
  searchableText?: string
  icon?: LucideIcon | ReactNode
  badge?: {
    labelEs: string
    labelEn?: string
    variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'
  }
  status?: {
    labelEs: string
    labelEn?: string
    variant: 'success' | 'warning' | 'error' | 'info'
    showIndicator?: boolean
  }
  recentlyUsed?: boolean
  frequency?: number
}

export interface BirhausSearchFilter {
  key: string
  labelEs: string
  labelEn?: string
  type: 'select' | 'multiselect' | 'boolean' | 'text' | 'date'
  options?: { value: string; labelEs: string; labelEn?: string }[]
  defaultValue?: any
  visible?: boolean
}

export interface BirhausSearchResult<T extends BirhausSearchItem = BirhausSearchItem> {
  items: T[]
  total: number
  hasMore: boolean
  suggestions?: string[]
  categories?: { [key: string]: number }
}

export interface BirhausSmartSearchProps<T extends BirhausSearchItem = BirhausSearchItem> {
  // BIRHAUS Principle #7: Spanish-first labels
  placeholderEs?: string
  placeholderEn?: string
  emptyStateMessageEs?: string
  emptyStateMessageEn?: string
  loadingMessageEs?: string
  loadingMessageEn?: string
  
  // Search configuration
  searchDelay?: number
  minSearchLength?: number
  maxResults?: number
  fuzzyThreshold?: number
  enableRecentSearches?: boolean
  enableSuggestions?: boolean
  
  // Multi-select configuration
  enableMultiSelect?: boolean
  maxSelections?: number
  showSelectedCount?: boolean
  
  // Filter configuration
  filters?: BirhausSearchFilter[]
  showFilters?: boolean
  enableAdvancedFilters?: boolean
  
  // Search function
  onSearch: (query: string, filters?: Record<string, any>) => Promise<BirhausSearchResult<T>>
  
  // Event handlers
  onSelect?: (item: T, selectedItems?: T[]) => void
  onMultiSelect?: (selectedItems: T[]) => void
  onClearSelection?: () => void
  onFiltersChange?: (filters: Record<string, any>) => void
  
  // Custom rendering
  renderItem?: (item: T, isSelected: boolean, isMultiSelect: boolean) => ReactNode
  renderEmptyState?: () => ReactNode
  renderLoadingState?: () => ReactNode
  
  // Styling
  className?: string
  dropdownClassName?: string
  itemClassName?: string
  
  // Initial state
  initialQuery?: string
  initialFilters?: Record<string, any>
  preselectedItems?: T[]
  
  // Accessibility
  ariaLabel?: string
  ariaDescription?: string
  
  // Auto-behavior
  autoFocus?: boolean
  clearOnSelect?: boolean
}

export interface BirhausSmartSearchHandle {
  focus: () => void
  clear: () => void
  getQuery: () => string
  getSelectedItems: () => BirhausSearchItem[]
  setQuery: (query: string) => void
}

export const BirhausSmartSearch = forwardRef(
  function BirhausSmartSearch<T extends BirhausSearchItem = BirhausSearchItem>({
    // Labels (Spanish-first)
    placeholderEs = "Buscar elementos...",
    placeholderEn,
    emptyStateMessageEs = "No se encontraron elementos para tu búsqueda",
    emptyStateMessageEn,
    loadingMessageEs = "Buscando...",
    loadingMessageEn,
    
    // Search configuration
    searchDelay = 300,
    minSearchLength = 2,
    maxResults = 7, // BIRHAUS: Miller's Law compliance
    fuzzyThreshold: _fuzzyThreshold = 0.6,
    enableRecentSearches = true,
    enableSuggestions = true,
    
    // Multi-select
    enableMultiSelect = false,
    maxSelections = 5, // BIRHAUS: Miller's Law for selections
    showSelectedCount: _showSelectedCount = true,
    
    // Filters
    filters = [],
    showFilters = false,
    enableAdvancedFilters = false,
    
    // Functions
    onSearch,
    onSelect,
    onMultiSelect,
    onClearSelection,
    onFiltersChange,
    
    // Custom rendering
    renderItem,
    renderEmptyState,
    renderLoadingState,
    
    // Styling
    className,
    dropdownClassName,
    itemClassName,
    
    // Initial state
    initialQuery = "",
    initialFilters = {},
    preselectedItems = [],
    
    // Accessibility
    ariaLabel,
    ariaDescription,
    
    // Auto-behavior
    autoFocus = false,
    clearOnSelect = !enableMultiSelect
  }: BirhausSmartSearchProps<T>, ref: React.Ref<BirhausSmartSearchHandle>) {
    
    // State management
    const [query, setQuery] = useState(initialQuery)
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [searchResults, setSearchResults] = useState<BirhausSearchResult<T>>({
      items: [],
      total: 0,
      hasMore: false
    })
    const [selectedItems, setSelectedItems] = useState<T[]>(preselectedItems)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const [_recentSearches, setRecentSearches] = useState<string[]>([])
    const [activeFilters, setActiveFilters] = useState(initialFilters)
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
    
    // Refs
    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchTimeout = useRef<NodeJS.Timeout>()
    
    // BIRHAUS: Expose imperative handle
    React.useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      clear: handleClear,
      getQuery: () => query,
      getSelectedItems: () => selectedItems,
      setQuery: (newQuery: string) => setQuery(newQuery)
    }), [query, selectedItems])

    // Effect: Auto-focus
    useEffect(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus()
      }
    }, [autoFocus])

    // Effect: Click outside to close
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target as Node) &&
          !inputRef.current?.contains(event.target as Node)
        ) {
          setIsOpen(false)
          setSelectedIndex(-1)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Effect: Debounced search
    useEffect(() => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }

      if (query.length >= minSearchLength) {
        searchTimeout.current = setTimeout(() => {
          performSearch(query, activeFilters)
        }, searchDelay)
      } else if (query.length === 0) {
        // Show recent searches or suggestions
        showInitialResults()
      } else {
        setSearchResults({ items: [], total: 0, hasMore: false })
        setIsOpen(false)
      }

      return () => {
        if (searchTimeout.current) {
          clearTimeout(searchTimeout.current)
        }
      }
    }, [query, activeFilters])

    // Perform search
    const performSearch = useCallback(async (searchQuery: string, filters: Record<string, any>) => {
      setLoading(true)
      try {
        const result = await onSearch(searchQuery, filters)
        
        // BIRHAUS: Enforce Miller's Law - limit to maxResults
        const limitedItems = result.items.slice(0, maxResults)
        
        setSearchResults({
          ...result,
          items: limitedItems
        })
        setIsOpen(true)
        setSelectedIndex(-1)
        
        // Add to recent searches if not already there
        if (enableRecentSearches && searchQuery.trim()) {
          setRecentSearches(prev => {
            const filtered = prev.filter(term => term !== searchQuery.trim())
            return [searchQuery.trim(), ...filtered].slice(0, 5) // Max 5 recent
          })
        }
      } catch (error) {
        console.error('BIRHAUS SmartSearch error:', error)
        setSearchResults({ items: [], total: 0, hasMore: false })
      } finally {
        setLoading(false)
      }
    }, [onSearch, maxResults, enableRecentSearches])

    // Show initial results (recent searches, suggestions)
    const showInitialResults = useCallback(async () => {
      if (!enableRecentSearches && !enableSuggestions) return

      try {
        // Could show recent items or popular items
        const result = await onSearch("", { ...activeFilters, showRecent: true })
        const limitedItems = result.items.slice(0, maxResults)
        
        setSearchResults({
          ...result,
          items: limitedItems
        })
        
        if (limitedItems.length > 0) {
          setIsOpen(true)
        }
      } catch (error) {
        // Silent fail for suggestions
      }
    }, [enableRecentSearches, enableSuggestions, onSearch, activeFilters, maxResults])

    // Handle item selection
    const handleItemSelect = (item: T) => {
      if (enableMultiSelect) {
        const isAlreadySelected = selectedItems.some(selected => selected.id === item.id)
        
        let newSelectedItems: T[]
        if (isAlreadySelected) {
          newSelectedItems = selectedItems.filter(selected => selected.id !== item.id)
        } else {
          if (selectedItems.length < maxSelections) {
            newSelectedItems = [...selectedItems, item]
          } else {
            // BIRHAUS: Cognitive load warning when exceeding Miller's Law
            console.warn('BIRHAUS Warning: Maximum selections exceeded (Miller\'s Law)')
            return
          }
        }
        
        setSelectedItems(newSelectedItems)
        onMultiSelect?.(newSelectedItems)
      } else {
        onSelect?.(item, selectedItems)
        if (clearOnSelect) {
          setQuery('')
          setIsOpen(false)
        }
      }
      
      setSelectedIndex(-1)
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isOpen || searchResults.items.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => 
            prev < searchResults.items.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < searchResults.items.length) {
            handleItemSelect(searchResults.items[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setSelectedIndex(-1)
          break
      }
    }

    // Clear search
    const handleClear = () => {
      setQuery('')
      setIsOpen(false)
      setSelectedIndex(-1)
      setSearchResults({ items: [], total: 0, hasMore: false })
      inputRef.current?.focus()
    }

    // Clear all selections
    const handleClearSelection = () => {
      setSelectedItems([])
      onClearSelection?.()
    }

    // Handle filter changes
    const handleFilterChange = (filterKey: string, value: any) => {
      const newFilters = { ...activeFilters, [filterKey]: value }
      setActiveFilters(newFilters)
      onFiltersChange?.(newFilters)
    }

    // Render item with default or custom renderer
    const renderSearchItem = (item: T, index: number) => {
      const isSelected = enableMultiSelect ? 
        selectedItems.some(selected => selected.id === item.id) :
        index === selectedIndex

      if (renderItem) {
        return renderItem(item, isSelected, enableMultiSelect)
      }

      return (
        <DefaultItemRenderer
          item={item}
          isSelected={isSelected}
          isMultiSelect={enableMultiSelect}
          className={itemClassName}
        />
      )
    }

    // Get current placeholder
    const placeholder = placeholderEn && typeof window !== 'undefined' && 
      navigator.language.startsWith('en') ? placeholderEn : placeholderEs

    return (
      <div className={cn("relative", className)} ref={dropdownRef}>
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              if (e.target.value.length > 0) {
                setIsOpen(true)
              }
            }}
            onFocus={() => {
              if (searchResults.items.length > 0 || query.length === 0) {
                setIsOpen(true)
                if (query.length === 0) {
                  showInitialResults()
                }
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              "w-full pl-10 pr-10 py-3 border rounded-lg",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
              "placeholder-gray-400 text-gray-900 bg-white",
              "transition-shadow duration-200"
            )}
            aria-label={ariaLabel}
            aria-describedby={ariaDescription ? "search-description" : undefined}
          />
          
          {/* Loading or Clear Button */}
          {loading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            </div>
          )}
          {!loading && query && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Limpiar búsqueda"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Multi-select indicators */}
        {enableMultiSelect && selectedItems.length > 0 && (
          <div className="mt-2 flex items-center justify-between text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600">
                {selectedItems.length} seleccionado{selectedItems.length !== 1 ? 's' : ''}
                {maxSelections && ` de ${maxSelections}`}
              </span>
            </div>
            <button
              onClick={handleClearSelection}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Limpiar selección
            </button>
          </div>
        )}

        {/* Filters */}
        {showFilters && filters.length > 0 && (
          <div className="mt-2">
            <SearchFilters
              filters={filters}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              showAdvanced={showAdvancedFilters}
              onToggleAdvanced={() => setShowAdvancedFilters(!showAdvancedFilters)}
              enableAdvanced={enableAdvancedFilters}
            />
          </div>
        )}

        {/* Results Dropdown */}
        {isOpen && (
          <div className={cn(
            "absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-96 overflow-auto",
            dropdownClassName
          )}>
            {loading && renderLoadingState ? (
              renderLoadingState()
            ) : loading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                {loadingMessageEn && typeof window !== 'undefined' && 
                  navigator.language.startsWith('en') ? loadingMessageEn : loadingMessageEs}
              </div>
            ) : searchResults.items.length === 0 ? (
              renderEmptyState ? (
                renderEmptyState()
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">
                    {query ? 
                      (emptyStateMessageEn && typeof window !== 'undefined' && 
                        navigator.language.startsWith('en') ? emptyStateMessageEn : emptyStateMessageEs) :
                      "Comienza a escribir para buscar..."
                    }
                  </p>
                </div>
              )
            ) : (
              <>
                {/* Results Header */}
                {query && (
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b">
                    Resultados ({searchResults.total})
                    {searchResults.hasMore && ` - Mostrando primeros ${maxResults}`}
                  </div>
                )}
                
                {/* Recent/Suggestions Header */}
                {!query && (
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b">
                    {enableRecentSearches ? "Recientes" : "Sugerencias"}
                  </div>
                )}

                {/* Results List */}
                {searchResults.items.map((item, index) => (
                  <div
                    key={item.id}
                    className={cn(
                      "border-b last:border-b-0 cursor-pointer transition-colors",
                      index === selectedIndex && "bg-blue-50",
                      enableMultiSelect && selectedItems.some(s => s.id === item.id) && "bg-blue-50"
                    )}
                    onClick={() => handleItemSelect(item)}
                  >
                    {renderSearchItem(item, index)}
                  </div>
                ))}
                
                {/* More results indicator */}
                {searchResults.hasMore && (
                  <div className="px-3 py-2 text-xs text-center text-gray-500 bg-gray-50">
                    Hay más resultados disponibles - refina tu búsqueda
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Screen reader description */}
        {ariaDescription && (
          <div id="search-description" className="sr-only">
            {ariaDescription}
          </div>
        )}
      </div>
    )
  }
)

// Default item renderer
function DefaultItemRenderer<T extends BirhausSearchItem>({
  item,
  isSelected,
  isMultiSelect,
  className
}: {
  item: T
  isSelected: boolean
  isMultiSelect: boolean
  className?: string
}) {
  const IconComponent = typeof item.icon === 'function' ? item.icon : null
  
  return (
    <div className={cn(
      "w-full px-3 py-3 text-left hover:bg-gray-50 flex items-start space-x-3",
      isSelected && "bg-blue-50",
      className
    )}>
      {/* Icon */}
      <div className="p-2 rounded-lg bg-gray-100 text-gray-600 flex-shrink-0">
        {IconComponent ? (
          <IconComponent className="w-4 h-4" />
        ) : typeof item.icon === 'object' ? (
          item.icon
        ) : (
          <Search className="w-4 h-4" />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="font-medium text-gray-900 truncate">{item.title}</p>
            {item.subtitle && (
              <p className="text-sm text-gray-500 truncate">{item.subtitle}</p>
            )}
          </div>
          
          {/* Badges and Status */}
          <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
            {item.recentlyUsed && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                Reciente
              </span>
            )}
            
            {item.badge && (
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full",
                item.badge.variant === 'success' && "bg-green-100 text-green-700",
                item.badge.variant === 'warning' && "bg-yellow-100 text-yellow-700",
                item.badge.variant === 'error' && "bg-red-100 text-red-700",
                item.badge.variant === 'info' && "bg-blue-100 text-blue-700",
                item.badge.variant === 'neutral' && "bg-gray-100 text-gray-700"
              )}>
                {item.badge.labelEs}
              </span>
            )}
            
            {isMultiSelect && isSelected && (
              <CheckCircle className="w-4 h-4 text-blue-600" />
            )}
          </div>
        </div>
        
        {/* Description */}
        {item.description && (
          <p className="mt-1 text-sm text-gray-600 line-clamp-2">{item.description}</p>
        )}
        
        {/* Status */}
        {item.status && (
          <div className="mt-2 flex items-center space-x-1 text-sm">
            {item.status.showIndicator && (
              <>
                {item.status.variant === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                {item.status.variant === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
                {item.status.variant === 'error' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                {item.status.variant === 'info' && <AlertTriangle className="w-4 h-4 text-blue-500" />}
              </>
            )}
            <span className={cn(
              item.status.variant === 'success' && "text-green-600",
              item.status.variant === 'warning' && "text-yellow-600", 
              item.status.variant === 'error' && "text-red-600",
              item.status.variant === 'info' && "text-blue-600"
            )}>
              {item.status.labelEs}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// Search filters component
function SearchFilters({
  filters,
  activeFilters,
  onFilterChange,
  showAdvanced,
  onToggleAdvanced,
  enableAdvanced
}: {
  filters: BirhausSearchFilter[]
  activeFilters: Record<string, any>
  onFilterChange: (key: string, value: any) => void
  showAdvanced: boolean
  onToggleAdvanced: () => void
  enableAdvanced: boolean
}) {
  const visibleFilters = filters.filter(f => f.visible !== false)
  const advancedFilters = filters.filter(f => f.visible === false)
  
  return (
    <div className="space-y-2">
      {/* Basic Filters */}
      {visibleFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {visibleFilters.map(filter => (
            <FilterControl
              key={filter.key}
              filter={filter}
              value={activeFilters[filter.key]}
              onChange={(value) => onFilterChange(filter.key, value)}
            />
          ))}
        </div>
      )}
      
      {/* Advanced Filters Toggle */}
      {enableAdvanced && advancedFilters.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={onToggleAdvanced}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
          >
            <Filter className="w-4 h-4" />
            <span>{showAdvanced ? "Ocultar" : "Mostrar"} filtros avanzados</span>
          </button>
          
          {showAdvanced && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {advancedFilters.map(filter => (
                <FilterControl
                  key={filter.key}
                  filter={filter}
                  value={activeFilters[filter.key]}
                  onChange={(value) => onFilterChange(filter.key, value)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Individual filter control
function FilterControl({
  filter,
  value,
  onChange
}: {
  filter: BirhausSearchFilter
  value: any
  onChange: (value: any) => void
}) {
  switch (filter.type) {
    case 'select':
      return (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="px-3 py-1 border rounded text-sm"
        >
          <option value="">{filter.labelEs}</option>
          {filter.options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.labelEs}
            </option>
          ))}
        </select>
      )
      
    case 'boolean':
      return (
        <label className="flex items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded"
          />
          <span>{filter.labelEs}</span>
        </label>
      )
      
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={filter.labelEs}
          className="px-3 py-1 border rounded text-sm"
        />
      )
      
    default:
      return null
  }
}

// Custom hook for smart search functionality
export function useBirhausSmartSearch<T extends BirhausSearchItem = BirhausSearchItem>() {
  const [selectedItems, setSelectedItems] = useState<T[]>([])
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  
  const addToRecent = useCallback((query: string) => {
    setRecentSearches(prev => {
      const filtered = prev.filter(term => term !== query)
      return [query, ...filtered].slice(0, 5)
    })
  }, [])
  
  const clearRecent = useCallback(() => {
    setRecentSearches([])
  }, [])
  
  const addSelection = useCallback((item: T) => {
    setSelectedItems(prev => {
      if (!prev.some(selected => selected.id === item.id)) {
        return [...prev, item]
      }
      return prev
    })
  }, [])
  
  const removeSelection = useCallback((itemId: string) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId))
  }, [])
  
  const clearSelections = useCallback(() => {
    setSelectedItems([])
  }, [])
  
  return {
    selectedItems,
    recentSearches,
    addToRecent,
    clearRecent,
    addSelection,
    removeSelection,
    clearSelections,
    setSelectedItems
  }
}

// Utility functions for search functionality
export const birhausSearchUtils = {
  /**
   * Simple fuzzy matching implementation
   * BIRHAUS: Cognitive load optimization through intelligent search
   */
  fuzzyMatch: (query: string, text: string, threshold: number = 0.6): boolean => {
    const queryLower = query.toLowerCase()
    const textLower = text.toLowerCase()
    
    // Exact match
    if (textLower.includes(queryLower)) return true
    
    // Character-by-character fuzzy matching
    let queryIndex = 0
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++
      }
    }
    
    const matchRatio = queryIndex / queryLower.length
    return matchRatio >= threshold
  },
  
  /**
   * Highlight matching text
   */
  highlightMatch: (text: string, query: string): string => {
    if (!query.trim()) return text
    
    const regex = new RegExp(`(${query.trim().split(' ').join('|')})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
  },
  
  /**
   * Sort items by relevance
   * BIRHAUS: Progressive disclosure - show most relevant first
   */
  sortByRelevance: <T extends BirhausSearchItem>(items: T[], query: string): T[] => {
    if (!query.trim()) {
      return items.sort((a, b) => (b.frequency || 0) - (a.frequency || 0))
    }
    
    return items.sort((a, b) => {
      const aTitle = a.title.toLowerCase()
      const bTitle = b.title.toLowerCase()
      const queryLower = query.toLowerCase()
      
      // Exact matches first
      const aExact = aTitle.includes(queryLower) ? 1 : 0
      const bExact = bTitle.includes(queryLower) ? 1 : 0
      if (aExact !== bExact) return bExact - aExact
      
      // Starts with query
      const aStarts = aTitle.startsWith(queryLower) ? 1 : 0
      const bStarts = bTitle.startsWith(queryLower) ? 1 : 0
      if (aStarts !== bStarts) return bStarts - aStarts
      
      // Recent usage
      const aRecent = a.recentlyUsed ? 1 : 0
      const bRecent = b.recentlyUsed ? 1 : 0
      if (aRecent !== bRecent) return bRecent - aRecent
      
      // Frequency
      return (b.frequency || 0) - (a.frequency || 0)
    })
  }
}